import axios from 'axios'
import { ElMessage, ElLoading } from 'element-plus'
import { useUserStore } from '@/stores/user'
import router from '@/router'

export const SERVER_HOST = import.meta.env.VITE_BASE_URL

// 创建 Axios 实例
const service = axios.create({
    baseURL: '/api', // 假设后端运行在 8000 端口
    timeout: 100000, // 请求超时时间
})

// 创建一个变量来存储 Loading 实例
let loadingInstance = null
// 记录正在进行中的请求数量
let pendingRequestCount = 0

// 启动全屏Loading
const startLoading = () => {
    if (!loadingInstance) {
        loadingInstance = ElLoading.service({
            lock: true,
            text: '服务器处理中，请稍后...',
            background: 'rgba(0, 0, 0, 0.7)',
            fullscreen: true
        })
    }
}

// 关闭全屏Loading
const closeLoading = () => {
    if (loadingInstance) {
        loadingInstance.close()
        loadingInstance = null
    }
}

// 请求拦截器
service.interceptors.request.use(
    config => {
        const userStore = useUserStore()
        if (userStore.token) {
            config.headers['Authorization'] = `Bearer ${userStore.token}`
        }

        // 判断是否为交易请求，可以根据URL路径或其他标识来判断
        // 这里假设所有 POST 请求都是交易请求，您可以根据实际情况调整
        if (config.method === 'post' || config.method === 'put' || config.method === 'delete') {
            // 增加请求计数
            pendingRequestCount++

            // 如果这是第一个交易请求，启动Loading
            if (pendingRequestCount === 1) {
                // 使用 setTimeout 避免频繁切换
                setTimeout(() => {
                    if (pendingRequestCount > 0 && !loadingInstance) {
                        startLoading()
                    }
                }, 300) // 300ms延迟，避免短暂请求也显示Loading
            }
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 响应拦截器
service.interceptors.response.use(
    response => {
        const config = response.config

        // 如果是交易请求，减少计数
        if (config.method === 'post' || config.method === 'put' || config.method === 'delete') {
            pendingRequestCount--

            // 如果没有进行中的交易请求了，关闭Loading
            if (pendingRequestCount <= 0) {
                pendingRequestCount = 0
                closeLoading()
            }
        }

        const res = response.data

        if (res.code !== 200) {
            ElMessage({
                message: res.message || '操作失败',
                type: 'error',
                duration: 5 * 1000
            })

            // 401: Token 失效或未登录 → 跳转登录页
            if (res.code === 401) {
                const userStore = useUserStore()
                userStore.logout() // 清除本地 Token 和用户信息

                // 提示登录失效
                ElMessage({
                    message: '登录已失效，请重新登录',
                    type: 'warning',
                    duration: 3 * 1000
                })

                // 跳转到登录页（带当前页面路径作为 redirect 参数，登录后可返回原页面）
                router.push({
                    path: '/login',
                    query: { redirect: router.currentRoute.value.fullPath } // 记录当前路径
                })
            }
            return Promise.reject(new Error(res.message || 'Error'))
        } else {
            return res
        }
    },
    error => {
        const config = error.config || {}

        // 如果是交易请求，减少计数
        if (config.method === 'post' || config.method === 'put' || config.method === 'delete') {
            pendingRequestCount--

            // 如果没有进行中的交易请求了，关闭Loading
            if (pendingRequestCount <= 0) {
                pendingRequestCount = 0
                closeLoading()
            }
        }

        console.log('err' + error) // for debug
        let message = error.message

        // 处理 HTTP 响应错误（包括 401 状态码）
        if (error.response) {
            // 后端返回的 401（Token 失效）
            if (error.response.status === 401) {
                const userStore = useUserStore()
                userStore.logout() // 清除本地 Token 和用户信息

                // 提示登录失效
                ElMessage({
                    message: '登录已失效，请重新登录',
                    type: 'warning',
                    duration: 3 * 1000
                })

                // 跳转到登录页（带重定向参数）
                router.push({
                    path: '/login',
                    query: { redirect: router.currentRoute.value.fullPath }
                })
            }

            // 其他错误信息提取
            if (error.response.data && error.response.data.detail) {
                message = error.response.data.detail
            } else if (error.response.data && error.response.data.message) {
                message = error.response.data.message
            }
        }

        ElMessage({
            message: message,
            type: 'error',
            duration: 5 * 1000
        })
        return Promise.reject(error)
    }
)

// 提供一个手动控制Loading的函数
export const showTransactionLoading = () => {
    pendingRequestCount++
    if (!loadingInstance) {
        startLoading()
    }
}

export const hideTransactionLoading = () => {
    pendingRequestCount--
    if (pendingRequestCount <= 0) {
        pendingRequestCount = 0
        closeLoading()
    }
}

export default service